"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.KicadNetNamingStrategy = exports.KICAD_PIN_ELECTRICAL_TYPE_KEY = void 0;
exports.kicadUnitSubReference = kicadUnitSubReference;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const IndividualGroupingRule_1 = require("../../NetCombobulation/IndividualGroupingRule");
const NetCombobulator_1 = require("../../NetCombobulation/NetCombobulator");
const NetNamingRuleChain_1 = require("../../NetCombobulation/NetNamingRuleChain");
exports.KICAD_PIN_ELECTRICAL_TYPE_KEY = 'kicad.electricalType';
function kicadUnitSubReference(unit) {
    let remaining = Math.floor(unit);
    let result = '';
    while (remaining > 0) {
        remaining--;
        result = String.fromCharCode('A'.charCodeAt(0) + (remaining % 26)) + result;
        remaining = Math.floor(remaining / 26);
    }
    return result;
}
class KicadNetNamingStrategy {
    constructor(sheetPath = '/', globalDriverNames = new Set()) {
        this.sheetPath = sheetPath;
        this.globalDriverNames = globalDriverNames;
        this.netNo = 0;
        this.implicitNetNames = new Map();
        this.unconnectedPinNetNames = new Map();
        this.pinHintNames = new Map();
        this.noConnectMarkers = [];
    }
    transformNetName(netObject) {
        if (netObject.label?.canNameNet) {
            return netObject.label.formattedLabel ?? netObject.label.originalLabel;
        }
        if (netObject.connection?.connection.appliesNetName?.label &&
            netObject.connection.connection.appliesNetName.labelType !== 'pin_hint') {
            return netObject.connection.connection.appliesNetName.label;
        }
        return undefined;
    }
    transformConnectivityName(netObject) {
        if (netObject.label?.labelType !== 'hier_label') {
            return undefined;
        }
        const escapedName = KicadNetNamingStrategy.escapeNetName(netObject.label.originalLabel);
        return this.globalDriverNames.has(escapedName) ? escapedName : this.sheetPath + escapedName;
    }
    canLabelTypeNameNets() {
        return true;
    }
    static getLabelPriority(type) {
        switch (type) {
            case 'global':
                return 6;
            case 'power_pin':
                return 5;
            case 'local_label':
                return 4;
            case 'hier_label':
                return 3;
            case 'sheet_pin':
                return 2;
            case 'pin':
            case 'pin_hint':
                return 1;
            case 'bus':
                return -1;
            default:
                return 0;
        }
    }
    resolveNetName(net) {
        const createCandidate = (label, type) => {
            const pinHint = this.pinHintNames.get(label);
            return {
                label: pinHint?.connected ?? label,
                ...(pinHint ?
                    {
                        netName: label,
                        unconnectedLabel: pinHint.unconnected,
                        unconnectedNetName: this.getUnconnectedPinHintName(label),
                    }
                    : {}),
                priority: KicadNetNamingStrategy.getLabelPriority(type),
                type,
            };
        };
        const labels = net.labels
            .filter((l) => l.canNameNet)
            .map((l) => createCandidate(l.formattedLabel ?? l.originalLabel, l.labelType))
            .concat(net.connections
            .map((c) => c.connection.appliesNetName ?
            createCandidate(c.connection.appliesNetName.label, c.connection.appliesNetName.labelType)
            : null)
            .filter((c) => c !== null))
            .sort((a, b) => KicadNetNamingStrategy.compareDriverCandidates(a, b));
        let namingLabel = labels?.filter((l) => l.type !== 'pin' && l.priority > KicadNetNamingStrategy.getLabelPriority('pin'))[0];
        if (namingLabel) {
            return namingLabel.label;
        }
        namingLabel = labels[0];
        if (namingLabel) {
            const isSoleDriver = net.labels.length === 0 && net.connections.length === 1;
            if (isSoleDriver || this.touchesNoConnectMarker(net)) {
                const unconnectedName = labels
                    .map((l) => ({
                    ...l,
                    label: l.unconnectedLabel ?? this.unconnectedPinNetNames.get(l.label) ?? l.label,
                    netName: l.unconnectedNetName ?? this.unconnectedPinNetNames.get(l.label) ?? l.netName,
                }))
                    .sort((a, b) => KicadNetNamingStrategy.compareDriverCandidates(a, b))[0];
                return unconnectedName.netName ?? unconnectedName.label;
            }
            return namingLabel.netName ?? namingLabel.label;
        }
        let implicitNetName = undefined;
        for (const obj of net.netObjects) {
            if (this.implicitNetNames.has(obj)) {
                implicitNetName = this.implicitNetNames.get(obj);
                break;
            }
        }
        if (!implicitNetName) {
            implicitNetName = `Net-${this.netNo++}`;
        }
        for (const obj of net.netObjects) {
            this.implicitNetNames.set(obj, implicitNetName);
        }
        return implicitNetName;
    }
    resolvePhysicalNetNames(hierarchy) {
        return new NetNamingRuleChain_1.NetNamingRuleChain().add(new IndividualGroupingRule_1.IndividualGroupingRule()).execute(hierarchy);
    }
    createVirtualRootHierarchy(roots) {
        if (roots.length === 0) {
            throw new Error('createVirtualRootHierarchy requires at least one root');
        }
        const virtualInstance = roots[0].instance;
        const emptyCombobulator = new NetCombobulator_1.NetCombobulator(new sigma_engine_shared_1.NetRefCtx(), this, virtualInstance, { autoAssignNets: false, pointRoundingPrecision: 0.1 });
        return {
            pathUniqueId: '__virtual_root__',
            hierarchyDepth: -1,
            instance: virtualInstance,
            combobulator: emptyCombobulator,
            children: roots,
        };
    }
    addNoConnectMarker(position, tolerance) {
        this.noConnectMarkers.push({ position, tolerance });
    }
    nameSymbolPin(pinInfo) {
        const name = this.getPinNetName(pinInfo);
        const unconnectedName = this.getPinNetName(pinInfo, true);
        if (name && unconnectedName) {
            this.unconnectedPinNetNames.set(name, unconnectedName);
        }
        return name;
    }
    nameSymbolPinHint(pinInfo, duplicateIndex) {
        const connected = this.getPinNetName(pinInfo);
        const unconnected = this.getPinNetName(pinInfo, true);
        if (!connected || !unconnected) {
            return connected;
        }
        const key = `${connected}__kicad_pin_hint_${duplicateIndex}`;
        const unconnectedKey = `${unconnected}__kicad_pin_hint_${duplicateIndex}`;
        this.pinHintNames.set(key, { connected, unconnected, declarationIndex: duplicateIndex, isUnconnected: false });
        this.pinHintNames.set(unconnectedKey, {
            connected,
            unconnected,
            declarationIndex: duplicateIndex,
            isUnconnected: true,
        });
        return key;
    }
    resolvePinHintNetNames(nets) {
        const winnersByName = new Map();
        for (const net of nets) {
            const name = String(net.name.strCast());
            const pinHint = this.pinHintNames.get(name);
            if (!pinHint) {
                continue;
            }
            const winners = winnersByName.get(pinHint.connected) ?? [];
            winners.push({ name, pinHint });
            winnersByName.set(pinHint.connected, winners);
        }
        const resolvedNames = new Map();
        for (const winners of winnersByName.values()) {
            winners.sort((left, right) => left.pinHint.declarationIndex - right.pinHint.declarationIndex);
            for (const [index, winner] of winners.entries()) {
                const suffix = index === winners.length - 1 ? '' : `_${index + 1}`;
                const baseName = winner.pinHint.isUnconnected ? winner.pinHint.unconnected : winner.pinHint.connected;
                resolvedNames.set(winner.name, `${baseName}${suffix}`);
            }
        }
        return resolvedNames;
    }
    getUnconnectedPinHintName(name) {
        const pinHint = this.pinHintNames.get(name);
        if (!pinHint) {
            return name;
        }
        return `${pinHint.unconnected}__kicad_pin_hint_${pinHint.declarationIndex}`;
    }
    getPinNetName(pinInfo, forceNoConnect = false) {
        const isUnconnected = forceNoConnect || pinInfo.electricalType === 'no_connect';
        const prefix = isUnconnected ? 'unconnected-(' : 'Net-(';
        const refDes = pinInfo.refDes;
        if (refDes.startsWith('#')) {
            return undefined;
        }
        if (!pinInfo.isAnnotated && pinInfo.symbolUuid) {
            return `${prefix}${pinInfo.symbolUuid}-Pad${pinInfo.pinNumber})`;
        }
        const pinShownName = KicadNetNamingStrategy.escapeNetName(pinInfo.pinName);
        const pinShownNumber = KicadNetNamingStrategy.escapeNetName(pinInfo.pinNumber);
        const hasPinName = pinShownName !== '~' && pinShownName.length > 0 && pinShownName !== pinShownNumber;
        if (hasPinName) {
            const unitRefDes = pinInfo.unitRefDes ?? refDes;
            if (isUnconnected || pinInfo.hasMultiplePinsWithSameName) {
                return `${prefix}${unitRefDes}-${pinShownName}-Pad${pinShownNumber})`;
            }
            return `${prefix}${unitRefDes}-${pinShownName})`;
        }
        return `${prefix}${refDes}-Pad${pinShownNumber})`;
    }
    static escapeNetName(text) {
        return text.replace(/\//g, '{slash}').replace(/[\r\n]/g, '');
    }
    touchesNoConnectMarker(net) {
        if (this.noConnectMarkers.length === 0) {
            return false;
        }
        const points = net.connections.map((c) => c.position).concat(net.wires.flatMap((w) => [w.from, w.to]));
        return this.noConnectMarkers.some(({ position, tolerance }) => points.some((p) => Math.abs(p[0] - position[0]) <= tolerance && Math.abs(p[1] - position[1]) <= tolerance));
    }
    static compareDriverCandidates(a, b) {
        if (b.priority !== a.priority) {
            return b.priority - a.priority;
        }
        const aIsLowQuality = (a.type === 'pin' || a.type === 'pin_hint') && a.label.includes('-Pad');
        const bIsLowQuality = (b.type === 'pin' || b.type === 'pin_hint') && b.label.includes('-Pad');
        if (aIsLowQuality !== bIsLowQuality) {
            return aIsLowQuality ? 1 : -1;
        }
        return KicadNetNamingStrategy.compareOrdinal(a.label, b.label);
    }
    static compareOrdinal(a, b) {
        if (a < b) {
            return -1;
        }
        return a > b ? 1 : 0;
    }
    static hasAutoGeneratedNetname(netName) {
        return netName.startsWith('Net-(') || netName.startsWith('unconnected-(');
    }
}
exports.KicadNetNamingStrategy = KicadNetNamingStrategy;
//# sourceMappingURL=KicadNetNamingStrategy.js.map